/**
 * Environment configuration utility
 * Automatically detects the current environment based on hostname
 * and returns appropriate configuration values
 */

export interface EnvironmentConfig {
  environment: 'local' | 'dev' | 'test' | 'prod';
  redirectUri: string;
  postLogoutRedirectUri: string;
  isLocal: boolean;
  isDev: boolean;
  isTest: boolean;
  isProd: boolean;
}

/**
 * Detects the current environment based on hostname
 */
export function detectEnvironment(): 'local' | 'dev' | 'test' | 'prod' {
  const hostname = window.location.hostname;
  
  // Local development
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.startsWith('192.168.') || hostname.endsWith('.local')) {
    return 'local';
  }
  
  // Production environment
  if (hostname.includes('prod')) {
    return 'prod';
  }

  // Test environment
  if (hostname.includes('test')) {
    return 'test';
  }
  
  // Dev environment (default for Azure deployments)
  if (hostname.includes('dev') || hostname.includes('azurewebsites.net')) {
    return 'dev';
  }
  
  // Fallback to local for unknown environments
  return 'local';
}

/**
 * Gets the complete environment configuration
 */
export function getEnvironmentConfig(): EnvironmentConfig {
  const environment = detectEnvironment();
  
  // Get redirect URIs based on environment
  let redirectUri: string;
  let postLogoutRedirectUri: string;
  redirectUri = import.meta.env.VITE_REDIRECT_URI || 'http://localhost:5050';
  postLogoutRedirectUri = import.meta.env.VITE_POST_LOGOUT_REDIRECT_URI || 'http://localhost:5050';
    
  return {
    environment,
    redirectUri,
    postLogoutRedirectUri,
    isLocal: environment === 'local',
    isDev: environment === 'dev', 
    isTest: environment === 'test',
    isProd: environment === 'prod'
  };
}

/**
 * Debug function to log current environment detection
 */
export function logEnvironmentInfo(): void {
  const config = getEnvironmentConfig();
  console.log('🌍 Environment Detection:', {
    hostname: window.location.hostname,
    detectedEnvironment: config.environment,
    redirectUri: config.redirectUri,
    postLogoutRedirectUri: config.postLogoutRedirectUri
  });
}
